import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    bomberosActividadesAPI, 
    bomberosCompaniaAPI, 
    bomberosMaterialMayorAPI,
    bomberosPersonalAPI 
} from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Save,
    CalendarDays,
    MapPin,
    Clock,
    Truck,
    Users,
    User,
    Loader2,
    Building2,
    Search,
} from 'lucide-react';

export default function ActividadFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const isEditing = !!id;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Data for selects
    const [companias, setCompanias] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [personal, setPersonal] = useState([]);
    
    // Form data
    const [formData, setFormData] = useState({
        actividad: '',
        fecha: '',
        hora_inicio: '',
        hora_fin: '',
        lugar: '',
        compania_id: '',
        material_mayor_ids: [],
        obac_id: '',
        asistentes_ids: [],
        observaciones_solicitante: '',
    });

    // Compañía del usuario (para filtros)
    const [userCompaniaId, setUserCompaniaId] = useState(null);
    const [searchAsistente, setSearchAsistente] = useState('');

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Cargar compañías
            const companiasRes = await bomberosCompaniaAPI.getAll();
            setCompanias(companiasRes.data);
            
            // Determinar compañía del usuario
            const companias_asignadas = user?.companias_asignadas || [];
            const defaultCompaniaId = companias_asignadas[0] || (companiasRes.data[0]?.id);
            setUserCompaniaId(defaultCompaniaId);
            
            // Cargar vehículos y personal (filtrados por compañía si hay una asignada)
            const [vehiculosRes, personalRes] = await Promise.all([
                bomberosMaterialMayorAPI.getAll(),
                bomberosPersonalAPI.getAll({ activo: true }),
            ]);
            
            // Filtrar por compañía del usuario si tiene una asignada
            if (companias_asignadas.length > 0) {
                setVehiculos(vehiculosRes.data.filter(v => companias_asignadas.includes(v.compania_id)));
                setPersonal(personalRes.data.filter(p => companias_asignadas.includes(p.compania_id)));
            } else {
                setVehiculos(vehiculosRes.data);
                setPersonal(personalRes.data);
            }
            
            // Si es edición, cargar datos de la actividad
            if (isEditing) {
                const actividadRes = await bomberosActividadesAPI.getById(id);
                const act = actividadRes.data;
                setFormData({
                    actividad: act.actividad || '',
                    fecha: act.fecha || '',
                    hora_inicio: act.hora_inicio || '',
                    hora_fin: act.hora_fin || '',
                    lugar: act.lugar || '',
                    compania_id: act.compania_id || '',
                    material_mayor_ids: act.material_mayor_ids || [],
                    obac_id: act.obac_id || '',
                    asistentes_ids: act.asistentes_ids || [],
                    observaciones_solicitante: act.observaciones_solicitante || '',
                });
            } else {
                // Nueva actividad - asignar compañía por defecto
                setFormData(prev => ({
                    ...prev,
                    compania_id: defaultCompaniaId || '',
                }));
            }
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.actividad || !formData.fecha) {
            toast.error('Complete los campos obligatorios');
            return;
        }
        
        setSaving(true);
        try {
            if (isEditing) {
                await bomberosActividadesAPI.update(id, formData);
                toast.success('Actividad actualizada');
            } else {
                await bomberosActividadesAPI.create(formData);
                toast.success('Actividad creada');
            }
            navigate('/bomberos/actividades');
        } catch (err) {
            console.error('Error saving:', err);
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleVehiculo = (vehiculoId) => {
        setFormData(prev => ({
            ...prev,
            material_mayor_ids: prev.material_mayor_ids.includes(vehiculoId)
                ? prev.material_mayor_ids.filter(id => id !== vehiculoId)
                : [...prev.material_mayor_ids, vehiculoId]
        }));
    };

    const toggleAsistente = (personalId) => {
        setFormData(prev => ({
            ...prev,
            asistentes_ids: prev.asistentes_ids.includes(personalId)
                ? prev.asistentes_ids.filter(id => id !== personalId)
                : [...prev.asistentes_ids, personalId]
        }));
    };

    // Filtrar vehículos y personal por compañía seleccionada
    const vehiculosFiltrados = formData.compania_id 
        ? vehiculos.filter(v => v.compania_id === formData.compania_id)
        : vehiculos;
    
    const personalFiltrado = formData.compania_id
        ? personal.filter(p => p.compania_id === formData.compania_id)
        : personal;

    if (loading) {
        return (
            <>
                <BomberosHeader />
                <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
                    <Skeleton className="h-10 w-64" />
                    <Skeleton className="h-96" />
                </div>
            </>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/actividades')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <CalendarDays className="h-6 w-6 text-orange-500" />
                            {isEditing ? 'Editar Actividad' : 'Nueva Actividad Extra Programática'}
                        </h1>
                        <p className="text-muted-foreground">
                            {isEditing ? 'Modificar solicitud de actividad' : 'Crear nueva solicitud de actividad'}
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <Card>
                        <CardHeader>
                            <CardTitle>Datos de la Actividad</CardTitle>
                            <CardDescription>Complete la información de la actividad extra programática</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Actividad y Compañía */}
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="actividad">Actividad *</Label>
                                    <Textarea
                                        id="actividad"
                                        placeholder="Descripción de la actividad..."
                                        value={formData.actividad}
                                        onChange={(e) => handleChange('actividad', e.target.value)}
                                        rows={3}
                                        required
                                        data-testid="input-actividad"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="compania">Compañía</Label>
                                    <Select 
                                        value={formData.compania_id || undefined} 
                                        onValueChange={(v) => {
                                            handleChange('compania_id', v);
                                            // Limpiar selecciones de vehículos y personal al cambiar compañía
                                            handleChange('material_mayor_ids', []);
                                            handleChange('obac_id', '');
                                            handleChange('asistentes_ids', []);
                                        }}
                                        disabled={user?.companias_asignadas?.length > 0}
                                    >
                                        <SelectTrigger data-testid="select-compania">
                                            <Building2 className="h-4 w-4 mr-2" />
                                            <SelectValue placeholder="Seleccione compañía" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {companias.map(c => (
                                                <SelectItem key={c.id} value={c.id}>
                                                    {c.numero ? `${c.numero}ª ` : ''}{c.nombre}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {user?.companias_asignadas?.length > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                            Compañía asignada automáticamente
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Fecha, Hora, Lugar */}
                            <div className="grid md:grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="fecha">Fecha *</Label>
                                    <Input
                                        id="fecha"
                                        type="date"
                                        value={formData.fecha}
                                        onChange={(e) => handleChange('fecha', e.target.value)}
                                        required
                                        data-testid="input-fecha"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="hora_inicio">Hora Inicio</Label>
                                    <Input
                                        id="hora_inicio"
                                        type="time"
                                        value={formData.hora_inicio}
                                        onChange={(e) => handleChange('hora_inicio', e.target.value)}
                                        data-testid="input-hora-inicio"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="hora_fin">Hora Fin</Label>
                                    <Input
                                        id="hora_fin"
                                        type="time"
                                        value={formData.hora_fin}
                                        onChange={(e) => handleChange('hora_fin', e.target.value)}
                                        data-testid="input-hora-fin"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lugar">Lugar</Label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="lugar"
                                            placeholder="Ubicación"
                                            value={formData.lugar}
                                            onChange={(e) => handleChange('lugar', e.target.value)}
                                            className="pl-9"
                                            data-testid="input-lugar"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* OBAC */}
                            <div className="space-y-2">
                                <Label htmlFor="obac">OBAC (Oficial/Bombero a Cargo)</Label>
                                <Select value={formData.obac_id || "none"} onValueChange={(v) => handleChange('obac_id', v === 'none' ? '' : v)}>
                                    <SelectTrigger data-testid="select-obac">
                                        <User className="h-4 w-4 mr-2" />
                                        <SelectValue placeholder="Seleccione responsable" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin asignar</SelectItem>
                                        {personalFiltrado.map(p => (
                                            <SelectItem key={p.id} value={p.id}>
                                                {p.grado && <Badge variant="outline" className="mr-2 text-xs">{p.grado}</Badge>}
                                                {p.nombres} {p.apellido_paterno}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Material Mayor (Multi-select) */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Truck className="h-4 w-4" />
                                    Material Mayor (Vehículos)
                                </Label>
                                <div className="border rounded-md p-4 max-h-48 overflow-y-auto">
                                    {vehiculosFiltrados.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">
                                            No hay vehículos disponibles para esta compañía
                                        </p>
                                    ) : (
                                        <div className="grid md:grid-cols-2 gap-2">
                                            {vehiculosFiltrados.map(v => (
                                                <div key={v.id} className="flex items-center space-x-2">
                                                    <Checkbox
                                                        id={`vehiculo-${v.id}`}
                                                        checked={formData.material_mayor_ids.includes(v.id)}
                                                        onCheckedChange={() => toggleVehiculo(v.id)}
                                                    />
                                                    <label
                                                        htmlFor={`vehiculo-${v.id}`}
                                                        className="text-sm cursor-pointer flex items-center gap-2"
                                                    >
                                                        <Badge variant="outline">{v.codigo}</Badge>
                                                        <span className="text-muted-foreground">{v.tipo}</span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {formData.material_mayor_ids.length > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        {formData.material_mayor_ids.length} vehículo(s) seleccionado(s)
                                    </p>
                                )}
                            </div>

                            {/* Asistentes (Multi-select) */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    Asistentes
                                </Label>
                                <div className="relative mb-2">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Buscar por nombre o RUT..."
                                        value={searchAsistente}
                                        onChange={(e) => setSearchAsistente(e.target.value)}
                                        className="pl-10 text-sm"
                                        data-testid="search-asistente"
                                    />
                                </div>
                                <div className="border rounded-md p-4 max-h-64 overflow-y-auto">
                                    {personalFiltrado.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">
                                            No hay personal disponible para esta compañía
                                        </p>
                                    ) : (
                                        <div className="grid md:grid-cols-2 gap-2">
                                            {personalFiltrado
                                                .filter(p => {
                                                    if (!searchAsistente) return true;
                                                    const term = searchAsistente.toLowerCase();
                                                    const fullName = `${p.nombres || ''} ${p.apellido_paterno || ''}`.toLowerCase();
                                                    return fullName.includes(term) || (p.rut || '').toLowerCase().includes(term);
                                                })
                                                .map(p => (
                                                <div key={p.id} className="flex items-center space-x-2">
                                                    <Checkbox
                                                        id={`asistente-${p.id}`}
                                                        checked={formData.asistentes_ids.includes(p.id)}
                                                        onCheckedChange={() => toggleAsistente(p.id)}
                                                    />
                                                    <label
                                                        htmlFor={`asistente-${p.id}`}
                                                        className="text-sm cursor-pointer"
                                                    >
                                                        {p.grado && <Badge variant="secondary" className="mr-1 text-xs">{p.grado}</Badge>}
                                                        {p.nombres} {p.apellido_paterno}
                                                    </label>
                                                </div>
                                            ))}
                                            {personalFiltrado.filter(p => {
                                                if (!searchAsistente) return true;
                                                const term = searchAsistente.toLowerCase();
                                                const fullName = `${p.nombres || ''} ${p.apellido_paterno || ''}`.toLowerCase();
                                                return fullName.includes(term) || (p.rut || '').toLowerCase().includes(term);
                                            }).length === 0 && searchAsistente && (
                                                <p className="text-sm text-muted-foreground col-span-2 text-center py-2">
                                                    Sin resultados para "{searchAsistente}"
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {formData.asistentes_ids.length > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        {formData.asistentes_ids.length} asistente(s) seleccionado(s)
                                    </p>
                                )}
                            </div>

                            {/* Observaciones */}
                            <div className="space-y-2">
                                <Label htmlFor="observaciones">Observaciones del Solicitante</Label>
                                <Textarea
                                    id="observaciones"
                                    placeholder="Observaciones adicionales..."
                                    value={formData.observaciones_solicitante}
                                    onChange={(e) => handleChange('observaciones_solicitante', e.target.value)}
                                    rows={2}
                                    data-testid="input-observaciones"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <div className="flex justify-end gap-4 mt-6">
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => navigate('/bomberos/actividades')}
                        >
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={saving} data-testid="submit-btn">
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    {isEditing ? 'Actualizar' : 'Crear Actividad'}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </>
    );
}
